import moment, { Moment } from "moment";
import React, { useMemo } from "react";

import { Spinner } from "src/shared/components";
import {
  blocksFromTop,
  sortComputedShifts,
} from "src/views/volunteer/shift-schedule/utils";

import { Calendar } from "../calendar/Calendar";
import { ShiftLegend } from "../components/ShiftLegend";
import { EVENT_DAYS, TShiftScheduleEvent } from "../constants";
import { DISABLE_REASONS, TShiftSchedule } from "../types";

interface CalenderViewScheduleProps {
  fetchingShifts: boolean;
  adminShifts: TShiftSchedule[];
  setSelectedShift: (shiftId: number) => void;
  setViewModal: (visible: boolean) => void;
}

const convertToScheduleShift = (
  shift: TShiftSchedule,
  momentStart: Moment,
  momentEnd: Moment,
  registered: number,
  max: number
): TShiftScheduleEvent => {
  const blocks = moment.duration(momentEnd.diff(momentStart)).asHours();

  return {
    id: shift.shift_id,
    title: shift.shift,
    category: shift.role,
    start: momentStart,
    end: momentEnd,
    displayedStart: momentStart.format("HH:mm"),
    displayedEnd: momentEnd.format("HH:mm"),
    numberOfBlocks: blocks,
    blocksFromTop: blocksFromTop(momentStart),
    overlapColumn: 0,
    registeredVolunteers: registered,
    maxVolunteers: max,
    registrationOpen: shift.registration_open,
    ...(shift.registration_open
      ? {}
      : {
          isDisableRegistered: {
            disabled: true,
            kind: DISABLE_REASONS.REGISTRATION_CLOSED,
            reason: "Registration isn't open",
          },
        }),
  };
};

const AdminShiftCalendarView: React.FC<CalenderViewScheduleProps> = ({
  fetchingShifts,
  adminShifts,
  setSelectedShift,
  setViewModal,
}) => {
  const handleShiftClick = (shift_id: number) => {
    setSelectedShift(shift_id);
    setViewModal(true);
  };

  const calendarShifts = useMemo(() => {
    if (fetchingShifts) return [];

    const computedShifts: TShiftScheduleEvent[] = adminShifts.map((shift) => {
      const momentStart = moment(shift.time_start).tz("America/New_York");
      const momentEnd = moment(shift.time_end).tz("America/New_York");
      const maxCount = shift.max_volunteer_count;

      return convertToScheduleShift(
        shift,
        momentStart,
        momentEnd,
        shift.signup_count,
        maxCount
      );
    });

    sortComputedShifts(computedShifts);
    return computedShifts;
  }, [fetchingShifts, adminShifts]);

  return (
    <>
      {fetchingShifts ? (
        <Spinner text="Loading shifts..." />
      ) : (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              minHeight: "70vh",
              height: "calc(100vh - 200px)",
            }}
          >
            <Calendar
              daysToShow={EVENT_DAYS}
              calendarShifts={calendarShifts}
              setSelectedShift={handleShiftClick}
            ></Calendar>
          </div>

          <ShiftLegend />
        </>
      )}
    </>
  );
};

export default AdminShiftCalendarView;
